/**
 * KbPipelineMonitor - Painel admin (root) para monitorar o pipeline
 * de indexação da Base de Conhecimento (KB).
 *
 * Aba 1: Visão geral (filas + scopes + volume 24h)
 * Aba 2: Por tenant (agregado com drill-down para páginas)
 * Aba 3: DLQ global (com reprocesso)
 *
 * Todo processamento é server-side via RPCs `kb_admin_*`.
 */
import { useMemo, useState } from "react";
import {
  Brain,
  RefreshCw,
  Search,
  Skull,
  ChevronRight,
  Trash2,
  Play,
  Building2,
  FileText,
  AlertTriangle,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from "@/components/layout/list-page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { PaginationFooter } from "@/components/ui/pagination-footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  useKbPipelineOverview,
  useKbPipelineVolume,
  useKbPipelineByTenant,
  useKbPipelineByPage,
  useKbPipelinePageDetail,
  useKbPipelineDlq,
  useKbReprocessDlqMutation,
  useKbPurgeFailedMutation,
  type KbTenantRow,
} from "@/hooks/admin/useKbPipeline";
import { KbHelpButton } from "@/components/admin/kb-pipeline/KbHelpButton";

// Formatação de UUID para exibição (8 chars uppercase)
const shortId = (id?: string | null) =>
  id ? id.slice(0, 8).toUpperCase() : "—";

const QUEUES = [
  "kb_embedding_jobs",
  "kb_transcription_jobs",
  "kb_import_jobs",
  "kb_reindex_jobs",
];

const SCOPE_COLORS: Record<string, string> = {
  chunkify: "hsl(var(--chart-1))",
  embed_primary: "hsl(var(--chart-2))",
  embed_secondary: "hsl(var(--chart-3))",
  reindex_full: "hsl(var(--chart-4))",
};

export default function KbPipelineMonitor() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("overview");

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Pipeline KB"
        description="Status das filas de indexação da Base de Conhecimento"
        actions={
          <div className="flex items-center gap-1">
            <KbHelpButton topic="root" label="Ajuda — Pipeline KB" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => qc.invalidateQueries({ queryKey: ["kb-admin"] })}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      <div className="px-4 pb-2">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex items-center gap-2 flex-wrap">
            <TabsList>
              <TabsTrigger value="overview">Visão geral</TabsTrigger>
              <TabsTrigger value="tenants">Por tenant</TabsTrigger>
              <TabsTrigger value="dlq">DLQ</TabsTrigger>
            </TabsList>
            {tab === "overview" && (
              <KbHelpButton topic="overview" label="Ajuda — Visão geral" />
            )}
            {tab === "tenants" && (
              <KbHelpButton topic="tenants" label="Ajuda — Por tenant" />
            )}
            {tab === "dlq" && (
              <KbHelpButton topic="dlq" label="Ajuda — DLQ" />
            )}
          </div>

          <TabsContent value="overview" className="mt-4">
            <OverviewTab />
          </TabsContent>
          <TabsContent value="tenants" className="mt-4">
            <TenantsTab />
          </TabsContent>
          <TabsContent value="dlq" className="mt-4">
            <DlqTab />
          </TabsContent>
        </Tabs>
      </div>
    </ListPageLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Visão Geral                                                                */
/* -------------------------------------------------------------------------- */
function OverviewTab() {
  const { data: overview = [], isLoading } = useKbPipelineOverview();
  const { data: volume = [] } = useKbPipelineVolume(60);
  const purge = useKbPurgeFailedMutation();

  const queueRows = overview.filter((r) => r.scope === "__total__");
  const scopeRows = overview.filter((r) => r.scope !== "__total__");

  // Agrupamento para o gráfico
  const chartData = useMemo(() => {
    const map = new Map<string, any>();
    for (const v of volume) {
      const key = v.bucket_at;
      const cur = map.get(key) ?? { bucket_at: key };
      cur[v.scope] = (cur[v.scope] ?? 0) + Number(v.created_count ?? 0);
      map.set(key, cur);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.bucket_at < b.bucket_at ? -1 : 1,
    );
  }, [volume]);

  const scopeKeys = Array.from(new Set(volume.map((v) => v.scope)));

  const totals = queueRows.reduce(
    (acc, r) => {
      acc.pending += Number(r.pending);
      acc.processing += Number(r.processing);
      acc.failed += Number(r.failed);
      acc.dlq += Number(r.dlq_total);
      return acc;
    },
    { pending: 0, processing: 0, failed: 0, dlq: 0 },
  );

  return (
    <div className="space-y-4">
      <ListPageKpis columns={4}>
        <KpiCard
          color="cyan"
          icon={RefreshCw}
          label="Pendentes"
          value={isLoading ? "…" : totals.pending}
        />
        <KpiCard
          color="yellow"
          icon={Play}
          label="Processando"
          value={isLoading ? "…" : totals.processing}
        />
        <KpiCard
          color="red"
          icon={AlertTriangle}
          label="Falhos"
          value={isLoading ? "…" : totals.failed}
        />
        <KpiCard
          color="purple"
          icon={Skull}
          label="DLQ"
          value={isLoading ? "…" : totals.dlq}
        />
      </ListPageKpis>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Filas</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Fila</TableHead>
                  <TableHead className="text-xs text-right">Pend.</TableHead>
                  <TableHead className="text-xs text-right">Proc.</TableHead>
                  <TableHead className="text-xs text-right">Falhos</TableHead>
                  <TableHead className="text-xs text-right">DLQ</TableHead>
                  <TableHead className="text-xs text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queueRows.map((r) => (
                  <TableRow key={r.queue_name}>
                    <TableCell className="text-xs font-mono">
                      {r.queue_name}
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      {r.pending}
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      {r.processing}
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      {r.failed > 0 ? (
                        <span className="text-destructive font-medium">
                          {r.failed}
                        </span>
                      ) : (
                        r.failed
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      {r.dlq_total > 0 ? (
                        <span className="text-destructive font-medium">
                          {r.dlq_total}
                        </span>
                      ) : (
                        r.dlq_total
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          purge.mutate({
                            queue: r.queue_name,
                            olderThanHours: 168,
                          })
                        }
                        disabled={purge.isPending}
                        title="Remover failed com mais de 7 dias"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {queueRows.length === 0 && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-6">
                      Sem dados
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              kb_embedding_jobs — por scope
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Scope</TableHead>
                  <TableHead className="text-xs text-right">Pend.</TableHead>
                  <TableHead className="text-xs text-right">Proc.</TableHead>
                  <TableHead className="text-xs text-right">Falhos</TableHead>
                  <TableHead className="text-xs text-right">DLQ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scopeRows.map((r) => (
                  <TableRow key={r.scope}>
                    <TableCell className="text-xs">
                      <Badge variant="outline">{r.scope}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      {r.pending}
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      {r.processing}
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      {r.failed}
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      {r.dlq_total}
                    </TableCell>
                  </TableRow>
                ))}
                {scopeRows.length === 0 && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-xs text-muted-foreground py-6">
                      Sem jobs ativos
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Volume 24h (jobs criados por scope, bucket de 1h)
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[280px] pt-2">
          {chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
              Sem atividade nas últimas 24h
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis
                  dataKey="bucket_at"
                  tickFormatter={(v: string) =>
                    new Date(v).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  }
                  fontSize={10}
                />
                <YAxis fontSize={10} allowDecimals={false} />
                <RTooltip
                  labelFormatter={(v: any) =>
                    new Date(v).toLocaleString()
                  }
                />
                <Legend wrapperStyle={{ fontSize: "10px" }} />
                {scopeKeys.map((k) => (
                  <Area
                    key={k}
                    type="monotone"
                    dataKey={k}
                    stackId="1"
                    stroke={SCOPE_COLORS[k] ?? "hsl(var(--primary))"}
                    fill={SCOPE_COLORS[k] ?? "hsl(var(--primary))"}
                    fillOpacity={0.4}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Por Tenant + Drill-down páginas                                            */
/* -------------------------------------------------------------------------- */
function TenantsTab() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const { data: rows = [], isLoading } = useKbPipelineByTenant({
    search,
    page,
    pageSize,
  });
  const total = rows[0]?.total_count ?? 0;

  const [selectedTenant, setSelectedTenant] = useState<KbTenantRow | null>(
    null,
  );

  return (
    <div className="space-y-3">
      <ListPageFiltersBar isFetching={isLoading}>
        <div className="relative flex-1 min-w-[220px] sm:flex-none sm:w-[280px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input
            className={cn(LIST_FILTERS_INPUT_CLASS, "pl-7 sm:pl-8")}
            placeholder="Buscar tenant…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </ListPageFiltersBar>

      <ListPageContent fullBleed>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Tenant</TableHead>
              <TableHead className="text-xs text-right">Páginas</TableHead>
              <TableHead className="text-xs text-right">Chunks</TableHead>
              <TableHead className="text-xs text-right">Embeddings</TableHead>
              <TableHead className="text-xs text-right">Pend.</TableHead>
              <TableHead className="text-xs text-right">Proc.</TableHead>
              <TableHead className="text-xs text-right">Falhos</TableHead>
              <TableHead className="text-xs text-right">DLQ</TableHead>
              <TableHead className="text-xs w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-xs text-muted-foreground py-8 text-center">
                  Carregando…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-xs text-muted-foreground py-8 text-center">
                  <Building2 className="h-8 w-8 mx-auto opacity-40 mb-1" />
                  Nenhum tenant com KB
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow
                  key={r.tenant_id}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => setSelectedTenant(r)}
                >
                  <TableCell className="text-xs">
                    <div className="font-medium">{r.tenant_name}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">
                      {shortId(r.tenant_id)}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-right">{r.published_pages}</TableCell>
                  <TableCell className="text-xs text-right">{r.total_chunks}</TableCell>
                  <TableCell className="text-xs text-right">{r.total_embeddings}</TableCell>
                  <TableCell className="text-xs text-right">{r.jobs_pending}</TableCell>
                  <TableCell className="text-xs text-right">{r.jobs_processing}</TableCell>
                  <TableCell className="text-xs text-right">
                    {r.jobs_failed > 0 ? (
                      <span className="text-destructive font-medium">{r.jobs_failed}</span>
                    ) : (
                      r.jobs_failed
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {r.dlq_total > 0 ? (
                      <span className="text-destructive font-medium">{r.dlq_total}</span>
                    ) : (
                      r.dlq_total
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    <ChevronRight className="h-3 w-3 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ListPageContent>

      <ListPageFooter>
        <PaginationFooter
          currentPage={page}
          pageSize={pageSize}
          totalItems={Number(total)}
          onPageChange={setPage} onPageSizeChange={() => {}}
        />
      </ListPageFooter>

      <TenantPagesSheet
        tenant={selectedTenant}
        onClose={() => setSelectedTenant(null)}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sheet: páginas do tenant                                                   */
/* -------------------------------------------------------------------------- */
function TenantPagesSheet({
  tenant,
  onClose,
}: {
  tenant: KbTenantRow | null;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);

  const { data: rows = [], isLoading } = useKbPipelineByPage({
    tenantId: tenant?.tenant_id ?? null,
    search,
    status,
    page,
    pageSize,
  });
  const total = rows[0]?.total_count ?? 0;

  return (
    <>
      <Sheet
        open={!!tenant}
        onOpenChange={(o) => {
          if (!o) {
            onClose();
            setSearch("");
            setStatus(null);
            setPage(1);
          }
        }}
      >
        <SheetContent className="w-full sm:max-w-[880px] p-0 flex flex-col">
          <SheetHeader className="px-4 py-3 border-b">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <SheetTitle className="text-base">
                  {tenant?.tenant_name}
                </SheetTitle>
                <SheetDescription className="text-xs font-mono">
                  {shortId(tenant?.tenant_id)}
                </SheetDescription>
              </div>
              <KbHelpButton topic="page" label="Ajuda — Páginas do tenant" />
            </div>
          </SheetHeader>

          <div className="px-4 py-2 border-b flex gap-2 flex-wrap items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                className={cn(LIST_FILTERS_INPUT_CLASS, "pl-7")}
                placeholder="Buscar página…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Select
              value={status ?? "__all"}
              onValueChange={(v) => {
                setStatus(v === "__all" ? null : v);
                setPage(1);
              }}
            >
              <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "w-[140px]")}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Todos status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="in_review">Em review</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Página</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs text-right">Chunks</TableHead>
                  <TableHead className="text-xs text-right">Emb.</TableHead>
                  <TableHead className="text-xs text-right">Pend.</TableHead>
                  <TableHead className="text-xs text-right">Falhos</TableHead>
                  <TableHead className="text-xs">Erro</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-xs text-center py-6 text-muted-foreground">
                      Carregando…
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-xs text-center py-6 text-muted-foreground">
                      Nenhuma página
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow
                      key={r.page_id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => setSelectedPageId(r.page_id)}
                    >
                      <TableCell className="text-xs">
                        <div className="font-medium truncate max-w-[220px]">{r.page_title}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {r.space_name ?? "—"} · {shortId(r.page_id)}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="outline">{r.page_status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-right">{r.chunks_count}</TableCell>
                      <TableCell className="text-xs text-right">{r.embeddings_count}</TableCell>
                      <TableCell className="text-xs text-right">{r.jobs_pending}</TableCell>
                      <TableCell className="text-xs text-right">
                        {r.jobs_failed > 0 ? (
                          <span className="text-destructive font-medium">{r.jobs_failed}</span>
                        ) : (
                          r.jobs_failed
                        )}
                      </TableCell>
                      <TableCell className="text-xs max-w-[220px] truncate text-destructive" title={r.last_error ?? undefined}>
                        {r.last_error ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="border-t px-4 py-2">
            <PaginationFooter
              currentPage={page}
              pageSize={pageSize}
              totalItems={Number(total)}
              onPageChange={setPage} onPageSizeChange={() => {}}
            />
          </div>
        </SheetContent>
      </Sheet>

      <PageDetailSheet
        pageId={selectedPageId}
        onClose={() => setSelectedPageId(null)}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Sheet: detalhe da página                                                   */
/* -------------------------------------------------------------------------- */
function PageDetailSheet({
  pageId,
  onClose,
}: {
  pageId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useKbPipelinePageDetail(pageId);
  const reprocess = useKbReprocessDlqMutation();

  const page = data?.page;
  const emb = data?.embeddings_by_dim ?? {};
  const jobs: any[] = data?.recent_jobs ?? [];
  const dlq: any[] = data?.dlq_items ?? [];
  const summary = data?.jobs_summary ?? {};

  return (
    <Sheet open={!!pageId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-[720px] p-0 flex flex-col">
        <SheetHeader className="px-4 py-3 border-b">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 shrink-0" />
                <span className="truncate">{page?.title ?? "…"}</span>
              </SheetTitle>
              <SheetDescription className="text-xs">
                {page?.space_name ?? "—"} · {shortId(pageId)} ·{" "}
                <Badge variant="outline" className="ml-1">
                  {page?.status}
                </Badge>
              </SheetDescription>
            </div>
            <KbHelpButton topic="pageDetail" label="Ajuda — Detalhe da página" />
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-auto p-4 space-y-4">
          {isLoading ? (
            <p className="text-xs text-muted-foreground">Carregando…</p>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-2">
                <StatBox label="Pend." value={summary.pending ?? 0} />
                <StatBox label="Proc." value={summary.processing ?? 0} />
                <StatBox label="Falhos" value={summary.failed ?? 0} accent="destructive" />
                <StatBox label="Concluídos" value={summary.done ?? 0} />
              </div>

              <div>
                <div className="text-xs font-medium mb-1">Chunks: {data?.chunks_count ?? 0}</div>
                <div className="grid grid-cols-4 gap-2">
                  {[768, 1024, 1536, 3072].map((dim) => (
                    <StatBox
                      key={dim}
                      label={`emb ${dim}`}
                      value={(emb as any)[dim] ?? (emb as any)[String(dim)] ?? 0}
                    />
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs font-medium mb-1">Últimos jobs</div>
                <div className="border rounded overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px]">Scope</TableHead>
                        <TableHead className="text-[10px]">Status</TableHead>
                        <TableHead className="text-[10px] text-right">Tent.</TableHead>
                        <TableHead className="text-[10px]">Atualizado</TableHead>
                        <TableHead className="text-[10px]">Erro</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {jobs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-[10px] text-center text-muted-foreground py-3">
                            Sem jobs
                          </TableCell>
                        </TableRow>
                      ) : (
                        jobs.map((j) => (
                          <TableRow key={j.id}>
                            <TableCell className="text-[10px]">
                              <Badge variant="outline" className="text-[9px] px-1 py-0">{j.scope}</Badge>
                            </TableCell>
                            <TableCell className="text-[10px]">{j.status}</TableCell>
                            <TableCell className="text-[10px] text-right">
                              {j.attempts}/{j.max_attempts}
                            </TableCell>
                            <TableCell className="text-[10px]">
                              {formatDistanceToNow(new Date(j.updated_at), { addSuffix: true, locale: ptBR })}
                            </TableCell>
                            <TableCell className="text-[10px] max-w-[180px] truncate text-destructive" title={j.last_error ?? undefined}>
                              {j.last_error ?? "—"}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div>
                <div className="text-xs font-medium mb-1">DLQ desta página</div>
                <div className="border rounded overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px]">Fila / Scope</TableHead>
                        <TableHead className="text-[10px]">Razão</TableHead>
                        <TableHead className="text-[10px]">Movido</TableHead>
                        <TableHead className="text-[10px] text-right">Ação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dlq.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-[10px] text-center text-muted-foreground py-3">
                            Sem itens em DLQ
                          </TableCell>
                        </TableRow>
                      ) : (
                        dlq.map((d) => (
                          <TableRow key={d.id}>
                            <TableCell className="text-[10px]">
                              <div className="font-mono">{d.queue_name}</div>
                              <div className="text-muted-foreground">{d.scope}</div>
                            </TableCell>
                            <TableCell className="text-[10px] max-w-[220px] truncate" title={d.last_error ?? d.reason}>
                              {d.reason ?? "—"}
                            </TableCell>
                            <TableCell className="text-[10px]">
                              {formatDistanceToNow(new Date(d.moved_at), { addSuffix: true, locale: ptBR })}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={!!d.reprocessed_at || reprocess.isPending}
                                onClick={() => reprocess.mutate(d.id)}
                              >
                                <Play className="h-3 w-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function StatBox({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: "destructive";
}) {
  return (
    <div className="border rounded px-2 py-1.5 text-center">
      <div
        className={cn(
          "text-lg font-semibold",
          accent === "destructive" && Number(value) > 0 && "text-destructive",
        )}
      >
        {value}
      </div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* DLQ global                                                                 */
/* -------------------------------------------------------------------------- */
function DlqTab() {
  const [queue, setQueue] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const { data: rows = [], isLoading } = useKbPipelineDlq({
    queue,
    page,
    pageSize,
  });
  const total = rows[0]?.total_count ?? 0;
  const reprocess = useKbReprocessDlqMutation();

  return (
    <div className="space-y-3">
      <ListPageFiltersBar isFetching={isLoading}>
        <Select
          value={queue ?? "__all"}
          onValueChange={(v) => {
            setQueue(v === "__all" ? null : v);
            setPage(1);
          }}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "w-[220px]")}>
            <SelectValue placeholder="Fila" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todas filas</SelectItem>
            {QUEUES.map((q) => (
              <SelectItem key={q} value={q}>
                {q}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      <ListPageContent fullBleed>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Fila / Scope</TableHead>
              <TableHead className="text-xs">Tenant</TableHead>
              <TableHead className="text-xs">Página</TableHead>
              <TableHead className="text-xs">Razão</TableHead>
              <TableHead className="text-xs">Movido</TableHead>
              <TableHead className="text-xs text-right">Ação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-xs text-center py-8 text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-xs text-center py-8 text-muted-foreground">
                  <Skull className="h-8 w-8 mx-auto opacity-40 mb-1" />
                  DLQ vazia
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.dlq_id}>
                  <TableCell className="text-xs">
                    <div className="font-mono">{r.queue_name}</div>
                    <div className="text-[10px] text-muted-foreground">{r.scope}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{r.tenant_name ?? "—"}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{shortId(r.tenant_id)}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="truncate max-w-[200px]">{r.page_title ?? "—"}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{shortId(r.page_id)}</div>
                  </TableCell>
                  <TableCell className="text-xs max-w-[280px] truncate" title={r.last_error ?? r.reason ?? undefined}>
                    {r.reason ?? "—"}
                  </TableCell>
                  <TableCell className="text-xs">
                    {formatDistanceToNow(new Date(r.moved_at), { addSuffix: true, locale: ptBR })}
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => reprocess.mutate(r.dlq_id)}
                      disabled={reprocess.isPending}
                    >
                      <Play className="h-3 w-3 mr-1" />
                      Reprocessar
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ListPageContent>

      <ListPageFooter>
        <PaginationFooter
          currentPage={page}
          pageSize={pageSize}
          totalItems={Number(total)}
          onPageChange={setPage} onPageSizeChange={() => {}}
        />
      </ListPageFooter>
    </div>
  );
}
